import { ga4Event } from '../utils/analytics';

/* Needs to declare this type because of a bug on Safari
 * More details here https://blog.cotten.io/desktop-safari-still-hates-instanceof-touchevent-1fcdda8409d5
 */
type AppTouchEvent = TouchEvent;
type CarouselInteractionType = 'arrow' | 'bullet' | 'progress' | 'scroll';

const sendEventCarouselInteraction = (interactionType: CarouselInteractionType) => {
    ga4Event({
        event: 'carousel_interaction',
        event_value: interactionType,
    });
};

export default () => {
    const carouselWrapperSelector = '.hero-wrapper';
    const carouselSelector = '.hero-wrapper .carousel';
    const cardSelector = '.hero-card';
    const controlButtonsSelector = '.hero-wrapper .control-button';
    const bulletControlSelector = '.hero-wrapper .bullet-control';
    const progressContainerSelector = '.hero-wrapper .time-progress-container';
    const cardProgressContainerSelector =
        '.hero-wrapper .time-progress-container .card-progress-container';
    const bulletControlId = 'bullet-control-';
    const cardProgressContainerId = 'time-progress-';
    const bulletControlActiveClass = 'bullet-control--active';
    const carouselDraggingClass = 'dragging';
    const dragSnapThreshold = 50;
    const autoplayTimeInterval = 7000;
    const infiniteScrollCloneCount = 2;

    const carousels = document.querySelectorAll(carouselSelector) as NodeListOf<HTMLElement>;
    const carouselWrappers = document.querySelectorAll(carouselWrapperSelector) as NodeListOf<HTMLElement>;
    
    if (!carousels || !carouselWrappers) return;

    carouselWrappers.forEach(wrapper => {
      const carousel = wrapper.querySelector(carouselSelector) as HTMLElement;
      if(!carousel) return;
      setupCarousel(carousel,wrapper);
    });

    

    function setupCarousel(carousel: HTMLElement,carouselWrapper: HTMLElement) {
        const initialCardsLength = carousel.querySelectorAll(cardSelector).length;

        const enableAutoplay = carousel.dataset.autoplay === 'true';
        const enableInfiniteScroll =
            carousel.dataset.infiniteScroll === 'true' &&
            initialCardsLength >= infiniteScrollCloneCount;

        const controlButtons = carouselWrapper.querySelectorAll(controlButtonsSelector);
        const bulletsControl = carouselWrapper.querySelectorAll(bulletControlSelector);
        const progressContainer = carouselWrapper.querySelector(progressContainerSelector);
        const cardProgressContainers =
            progressContainer?.querySelectorAll(cardProgressContainerSelector) ?? [];

        let isDragging = false;
        let isMouseHovering = false;
        let startDragX: number;
        let startDragY: number;
        let lastDragX: number;
        let lastMoveDirection: number = 0;
        let currentScroll: number = 0;
        let currentIndex: number = 0;
        let isLastCard: boolean = false;
        let currentIndexScroll: number = 0;
        let autoPlayTimeout:number | null;
        let timeoutStartTime: number;
        let timeoutRemainingTime: number = autoplayTimeInterval;
        let isAutoPlayPaused = true;
        let isTouchEvent = false;
        let dragDirectionDetected = false;
        let isHorizontalDrag = false;

        function clampIndex(index: number, length: number) {
            return Math.min(Math.max(index, 0), length - 1);
        }

        function clampScroll(
            index: number,
            length: number,
            cardWidth: number,
            carouselGap: number
        ) {
            const maxScroll = carouselGap;
            const minScroll = -1 * ((length - 1) * cardWidth - carouselGap);
            let snapScroll = -1 * (index * cardWidth - carouselGap);

            return Math.min(Math.max(snapScroll, minScroll), maxScroll);
        }

        const snapToIndex = (index: number) => {
            const carouselWidth = carousel?.clientWidth || 0;
            const cards = Array.from(carousel.querySelectorAll(cardSelector));

            const clampedIndex = clampIndex(index, cards.length);

            const cardWidth = cards[0]?.clientWidth || 0;
            const carouselGap = (carouselWidth - cardWidth) / 2;
            const cardsGap = Math.abs(
                cards[0]?.getBoundingClientRect().right - cards[1]?.getBoundingClientRect().left ||
                    0
            );
            const cardWidthOffset = cardWidth + cardsGap;

            const clampedSnapScroll = clampScroll(
                clampedIndex,
                cards.length,
                cardWidthOffset,
                carouselGap
            );

            const indexChanged = currentIndex !== clampedIndex;
            carousel.style.transform = `translateX(${clampedSnapScroll}px)`;
            currentIndexScroll = currentScroll = clampedSnapScroll;
            currentIndex = clampedIndex;
            isLastCard = currentIndex === cards.length - 1;

            if (indexChanged) {
                if (enableInfiniteScroll) {
                    let applyInfiniteScroll = false;
                    if (currentIndex < infiniteScrollCloneCount) {
                        applyInfiniteScroll = true;
                        currentIndex = cards.length - 1 - infiniteScrollCloneCount;
                    }

                    if (currentIndex > cards.length - 1 - infiniteScrollCloneCount) {
                        applyInfiniteScroll = true;
                        currentIndex = infiniteScrollCloneCount;
                    }

                    if (applyInfiniteScroll) {
                        setTimeout(() => {
                            carousel.classList.add('no-transition');
                            snapToIndex(currentIndex);
                            setTimeout(() => carousel.classList.remove('no-transition'), 10);
                        }, 500);
                    }
                }

                resetAutoPlay();
                carousel.dispatchEvent(new CustomEvent('carouselindexchange'));
            }
        };

        const dragStart = (e: MouseEvent | AppTouchEvent) => {
            // Reset drag direction detection for new drag
            dragDirectionDetected = false;
            isHorizontalDrag = false;
            
            // For mouse events, immediately start dragging (desktop behavior)
            if (e instanceof MouseEvent) {
                isDragging = true;
                carousel.classList.add(carouselDraggingClass);
                pauseAutoPlay();
                startDragX = e.pageX;
                startDragY = e.pageY;
                lastDragX = startDragX;
                return;
            }

            // For touch events, capture coordinates but don't start dragging yet
            if ('touches' in e) {
                isTouchEvent = true;
                startDragX = e.touches[0].pageX;
                startDragY = e.touches[0].pageY;
                lastDragX = startDragX;
            }
        };

        const dragging = (e: MouseEvent | AppTouchEvent) => {
            let pageX: number = 0;
            let pageY: number = 0;
            
            if ('touches' in e) {
                pageX = e.touches[0].pageX;
                pageY = e.touches[0].pageY;
            }

            if (e instanceof MouseEvent) {
                pageX = e.pageX;
                pageY = e.pageY;
            }

            // For touch events, detect drag direction first
            if (isTouchEvent && !dragDirectionDetected) {
                const deltaX = Math.abs(pageX - startDragX);
                const deltaY = Math.abs(pageY - startDragY);
                
                // Only detect direction if there's meaningful movement
                if (deltaX > 10 || deltaY > 10) {
                    dragDirectionDetected = true;
                    
                    // Determine if this is a horizontal or vertical drag
                    // Consider it horizontal if horizontal movement is greater than vertical
                    // Apply a slight bias towards horizontal for carousel interaction
                    isHorizontalDrag = deltaX > deltaY * 0.75;
                    
                    if (isHorizontalDrag) {
                        isDragging = true;
                        carousel.classList.add(carouselDraggingClass);
                        pauseAutoPlay();
                    } else {
                        // This is vertical scrolling, don't interfere with page scroll
                        return;
                    }
                } else {
                    // Not enough movement yet, don't process
                    return;
                }
            }

            // If we're not dragging (either mouse not dragging or touch determined as vertical), return
            if (!isDragging) return;
            
            // Prevent default only when we're handling horizontal carousel dragging
            e.preventDefault();

            const delta = Math.abs(pageX - lastDragX);
            if (delta === 0) {
                lastMoveDirection = 0;
                return;
            }

            const isDraggingLeft = pageX - lastDragX < 0;
            lastMoveDirection = isDraggingLeft ? -1 : 1;

            currentScroll = isDraggingLeft ? currentScroll - delta : currentScroll + delta;
            carousel.style.transform = `translateX(${currentScroll}px)`;
            lastDragX = pageX;
        };

        const dragStop = (e: MouseEvent | AppTouchEvent) => {
            // Handle mixed mouse/touch events
            if (e instanceof MouseEvent && isTouchEvent) {
                isTouchEvent = false;
                isMouseHovering = false;
            }

            // Reset touch-specific flags for touch events
            if ('touches' in e || isTouchEvent) {
                isTouchEvent = false;
                dragDirectionDetected = false;
                isHorizontalDrag = false;
            }

            // If we weren't dragging (e.g., vertical scroll was detected), just return
            if (!isDragging) {
                return;
            }

            isDragging = false;
            carousel.classList.remove(carouselDraggingClass);

            if (!isMouseHovering && isAutoPlayPaused) autoPlay();

            if (lastMoveDirection === 0) {
                if (currentIndexScroll !== currentScroll) {
                    snapToIndex(currentIndex);
                }

                return;
            }

            const movedLeft = lastMoveDirection < 0;
            const movedRight = lastMoveDirection > 0;
            const movedDelta = Math.abs(startDragX - lastDragX);

            const cards = Array.from(carousel.querySelectorAll(cardSelector));
            const cardWidth = cards[0]?.clientWidth || 0;
            let snapIndex = clampIndex(
                Math.round(Math.abs(currentScroll) / cardWidth),
                cards.length
            );

            if (
                currentIndex === snapIndex &&
                lastMoveDirection !== 0 &&
                movedDelta > dragSnapThreshold
            ) {
                const isFirstCard = currentIndex === 0;
                const isLastCard = currentIndex === cards.length - 1;

                if (movedRight && !isFirstCard) {
                    snapIndex--;
                }

                if (movedLeft && !isLastCard) {
                    snapIndex++;
                }

                if (snapIndex !== currentIndex) {
                    sendEventCarouselInteraction('scroll');
                }
            }

            snapToIndex(snapIndex);
            lastMoveDirection = 0;
        };

        function autoPlay() {
            if (!enableAutoplay || isMouseHovering) return;
            if (autoPlayTimeout) window.clearTimeout(autoPlayTimeout);
            timeoutStartTime = new Date().getTime();
            isAutoPlayPaused = false;
            autoPlayTimeout = window.setTimeout(() => {
                const cards = Array.from(carousel.querySelectorAll(cardSelector));
                let nextIndex = currentIndex + 1;
                if (nextIndex >= cards.length) nextIndex = 0;
                snapToIndex(nextIndex);
            }, timeoutRemainingTime);

            carousel.dispatchEvent(new CustomEvent('carouselautoplaystart'));
        }

        function pauseAutoPlay() {
            if (isAutoPlayPaused) return;
            isAutoPlayPaused = true;
            if (autoPlayTimeout) window.clearTimeout(autoPlayTimeout);
            timeoutRemainingTime -= new Date().getTime() - timeoutStartTime;

            carousel.dispatchEvent(new CustomEvent('carouselautoplaypause'));
        }

        function resetAutoPlay() {
            if (autoPlayTimeout) window.clearTimeout(autoPlayTimeout);
            timeoutRemainingTime = autoplayTimeInterval;
            autoPlay();
        }

        function handleMouseEnter() {
            isMouseHovering = true;
            pauseAutoPlay();
        }

        function handleMouseLeave() {
            isMouseHovering = false;
            autoPlay();
        }

        progressContainer?.classList.toggle('hidden', !enableAutoplay);

        // Setup infinite scroll
        if (enableInfiniteScroll) {
            const cards = Array.from(carousel.querySelectorAll(cardSelector));

            // Insert copy of last cards at the beginning
            cards
                .slice(-infiniteScrollCloneCount)
                .reverse()
                .forEach((card) => {
                    carousel.insertAdjacentHTML('afterbegin', card.outerHTML);
                    carousel.firstElementChild?.setAttribute('data-clone', 'true');
                    carousel.firstElementChild?.querySelectorAll('img').forEach((img) => {
                        img.dataset.src = img.src;
                        img.setAttribute('loading', 'lazy');
                    });
                });

            // Insert copy of first cards at the end
            cards.slice(0, infiniteScrollCloneCount).forEach((card) => {
                carousel.insertAdjacentHTML('beforeend', card.outerHTML);
                carousel.lastElementChild?.setAttribute('data-clone', 'true');
                carousel.lastElementChild?.querySelectorAll('img').forEach((img) => {
                    img.dataset.src = img.src;
                    img.setAttribute('loading', 'lazy');
                });
            });

            carousel.classList.add('no-transition');
            snapToIndex(infiniteScrollCloneCount);
            setTimeout(() => carousel.classList.remove('no-transition'), 10);
        }

        // Setup arrow buttons
        controlButtons.forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const nextIndex = button.id === 'left' ? currentIndex - 1 : currentIndex + 1;
                snapToIndex(nextIndex);
                sendEventCarouselInteraction('arrow');
            });

            carousel.addEventListener('carouselindexchange', () => {
                if (button.id === 'left') {
                    button.classList.toggle('hidden', currentIndex === 0);
                }

                if (button.id === 'right') {
                    button.classList.toggle('hidden', isLastCard);
                }
            });
        });

        // Setup bullet buttons
        bulletsControl.forEach((bullet) => {
            const bulletIndex =
                parseInt(bullet.id.replace(bulletControlId, ''), 10) +
                (enableInfiniteScroll ? infiniteScrollCloneCount : 0);
            bullet.addEventListener('click', (e) => {
                e.preventDefault();
                const nextIndex = bulletIndex;
                snapToIndex(nextIndex);
                sendEventCarouselInteraction('bullet');
            });

            carousel.addEventListener('carouselindexchange', () => {
                bullet.classList.toggle(bulletControlActiveClass, currentIndex === bulletIndex);
            });
        });

        // Setup progress bars
        cardProgressContainers.forEach((progressContainer) => {
            if (!enableAutoplay) return;
            const cardIndex =
                parseInt(progressContainer.id.replace(cardProgressContainerId, ''), 10) +
                (enableInfiniteScroll ? infiniteScrollCloneCount : 0);
            let animation: Animation | undefined;

            progressContainer.addEventListener('click', (e) => {
                e.preventDefault();
                const nextIndex = cardIndex;
                snapToIndex(nextIndex);
                sendEventCarouselInteraction('progress');
            });

            carousel.addEventListener('carouselindexchange', () => {
                progressContainer.classList.toggle('filled', currentIndex > cardIndex);

                if (currentIndex !== cardIndex && animation) {
                    animation.cancel();
                }
            });

            carousel.addEventListener('carouselautoplaystart', () => {
                if (currentIndex !== cardIndex || isAutoPlayPaused) return;
                if (animation && animation.playState === 'paused') {
                    return animation.play();
                }

                if (animation) animation.cancel();
                animation = progressContainer.firstElementChild!.animate(
                    [{ width: '0%' }, { width: '100%' }],
                    { duration: timeoutRemainingTime, fill: 'forwards' }
                );
                animation!.onfinish = function () {
                    setTimeout(() => this.cancel(), 50);
                };
            });

            carousel.addEventListener('carouselautoplaypause', () => {
                if (animation && animation.playState === 'running') {
                    animation.pause();
                }
            });
        });

        // Setup events
        carousel.addEventListener('mousedown', dragStart);
        carousel.addEventListener('touchstart', dragStart);
        carousel.addEventListener('mousemove', dragging);
        carousel.addEventListener('touchmove', dragging);
        document.addEventListener('mouseup', dragStop);
        document.addEventListener('touchend', dragStop);
        carouselWrapper.addEventListener('mouseenter', handleMouseEnter);
        carouselWrapper.addEventListener('mouseleave', handleMouseLeave);

        window.addEventListener('resize', () => snapToIndex(currentIndex));

        // Set initial index
        carousel.dispatchEvent(new CustomEvent('carouselindexchange'));
        autoPlay();

        const lazyLoadObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                lazyLoadObserver.unobserve(entry.target);
                const imgElement = entry.target as HTMLImageElement;
                const src = imgElement.dataset.src ?? '';
                imgElement.src = src;
            });
        });

        carousel.querySelectorAll('.hero-card img[loading="lazy"]').forEach((img) => {
            lazyLoadObserver.observe(img);
        });
    }
};
